

import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import FlighTabs from "../AllTabs/FlightTabs";
import airports from "../../Data/AirportList.json";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  RiSearchLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiCalendarLine,
  RiUserLine,
  RiFilterLine,
  RiSortAsc,
  RiSortDesc,
  RiPriceTagLine,
  RiBuildingLine,
  RiCalendarEventLine,
  RiArrowUpDownLine,
  RiArrowRightLine,
  RiTimerLine,
  RiUserSharedLine,
  RiShoppingCartLine,
  RiAlertLine,
  RiArrowLeftLine,
  RiLoader4Line,
  RiInformationLine,
  RiPlaneLine
} from "react-icons/ri";

const SeriesFareAgentSearch = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Search form states
  const [fromSearch, setFromSearch] = useState("");
  const [toSearch, setToSearch] = useState("");
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [filteredFromAirports, setFilteredFromAirports] = useState([]);
  const [filteredToAirports, setFilteredToAirports] = useState([]);
  const [selectedFromAirport, setSelectedFromAirport] = useState(null);
  const [selectedToAirport, setSelectedToAirport] = useState(null);
  const [travelDate, setTravelDate] = useState(new Date());
  const [passengers, setPassengers] = useState(1);
  
  // Search results
  const [searchResponse, setSearchResponse] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [autoSearchTriggered, setAutoSearchTriggered] = useState(false);
  
  // Filter and sort
  const [priceSort, setPriceSort] = useState("asc");
  const [airlineFilter, setAirlineFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);

  // Get user's currency
  const userCurrency = user?.currency || 'INR';

  // Initialize top airports
  const topAirports = airports
    .filter(airport => airport && airport.display === true)
    .slice(0, 10);

  // Custom date input
  const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="input-group">
      <input
        type="text"
        className="form-control"
        value={value}
        onClick={onClick}
        ref={ref}
        readOnly
        placeholder="Select date"
      />
      <button className="btn btn-secondary" type="button" onClick={onClick}>
        <RiCalendarLine />
      </button>
    </div>
  ));

  // Find airport by code
  const findAirportByCode = (code) => {
    if (!code) return null;
    const upperCode = code.toUpperCase();
    return airports.find(airport => 
      airport.airportCode === upperCode || 
      airport.airportCode === code
    );
  };

  // Format date string
  const formatDateString = (dateStr) => {
    if (!dateStr) return new Date();
    try {
      if (typeof dateStr === 'string') {
        if (dateStr.includes('T')) {
          return new Date(dateStr);
        }
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          return new Date(parts[0], parts[1] - 1, parts[2]);
        }
        return new Date(dateStr);
      }
      if (dateStr instanceof Date) {
        return dateStr;
      }
      return new Date();
    } catch (error) {
      console.error("Error parsing date:", error);
      return new Date();
    }
  };

  // Perform search
  const performSearch = async (fromCode, toCode, date, pax) => {
    console.log("Searching:", { from: fromCode, to: toCode, date: date, passengers: pax });
    
    setLoadingResults(true);
    
    try {
      const formattedDate = date.toISOString().split('T')[0];
      
      const response = await apiService.getAll(
        `${config.SERIESFARESEARCH}?from=${fromCode}&to=${toCode}&date=${formattedDate}&pax=${pax}`
      );
      
      console.log("Search response:", response);
      
      if (response) {
        if (response.count !== undefined && response.fares !== undefined) {
          setSearchResponse(response);
          setSearchResults(response.fares || []);
          setFilteredResults(response.fares || []);
        } else if (Array.isArray(response)) {
          setSearchResponse({ count: response.length, fares: response });
          setSearchResults(response);
          setFilteredResults(response);
        } else {
          setSearchResponse(null);
          setSearchResults([]);
          setFilteredResults([]);
        }
        
        setAirlineFilter("");
        setCompanyFilter("");
      } else {
        setSearchResponse(null);
        setSearchResults([]);
        setFilteredResults([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      if (!autoSearchTriggered) {
        alert("Error searching for series fares. Please try again.");
      }
      setSearchResponse(null);
      setSearchResults([]);
      setFilteredResults([]);
    } finally {
      setLoadingResults(false);
      setSearchPerformed(true);
    }
  };


const handleBlockFare = (fare) => {
  // Navigate to block confirmation screen
  navigate("/SeriesFare/blockConfirmation", {
    state: {
      fare: fare,
      searchQuery: {
        from: selectedFromAirport?.airportCode,
        to: selectedToAirport?.airportCode,
        date: travelDate.toISOString().split('T')[0]
      },
      bookingPax: passengers,
    }
  });
};
  // Initialize and search
  useEffect(() => {
    const initializeAndSearch = async () => {
      if (location.state?.searchQuery) {
        const { searchQuery, passengerCount } = location.state;
        
        console.log("Initializing with location state:", searchQuery);
        
        const fromCode = searchQuery.from?.toUpperCase() || '';
        const toCode = searchQuery.to?.toUpperCase() || '';
        const date = searchQuery.date ? formatDateString(searchQuery.date) : new Date();
        const pax = passengerCount || searchQuery.pax || 1;
        
        setPassengers(pax);
        setTravelDate(date);
        
        const fromAirport = findAirportByCode(fromCode);
        if (fromAirport) {
          setSelectedFromAirport(fromAirport);
          setFromSearch(`${fromAirport.cityName} (${fromAirport.airportCode})`);
        } else {
          setFromSearch(fromCode);
        }
        
        const toAirport = findAirportByCode(toCode);
        if (toAirport) {
          setSelectedToAirport(toAirport);
          setToSearch(`${toAirport.cityName} (${toAirport.airportCode})`);
        } else {
          setToSearch(toCode);
        }
        
        setAutoSearchTriggered(true);
        setSearchPerformed(true);
        
        if (fromCode && toCode && date) {
          console.log("Triggering immediate search...");
          performSearch(fromCode, toCode, date, pax);
        } else {
          console.log("Missing required parameters for auto-search");
          setLoadingResults(false);
        }
      }
    };
    
    initializeAndSearch();
  }, [location.state]);

  // Handle travel date change
  const handleTravelDateChange = (date) => {
    if (date) {
      setTravelDate(date);
    }
  };

  // Filter airports
  const filterAirports = (searchTerm, setFilteredFunction) => {
    if (searchTerm.trim() === "") {
      setFilteredFunction(topAirports);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = airports.filter(airport => {
        if (!airport) return false;
        
        const airportCode = (airport.airportCode || '').toLowerCase();
        const cityName = (airport.cityName || '').toLowerCase();
        const airportName = (airport.airportName || '').toLowerCase();
        
        return airportCode.includes(term) ||
          cityName.includes(term) ||
          airportName.includes(term);
      }).slice(0, 10);
      setFilteredFunction(filtered);
    }
  };

  // Handle airport selection
  const handleAirportSelect = (airport, type) => {
    const displayValue = `${airport.cityName || ''} (${airport.airportCode || ''})`;
    
    if (type === 'from') {
      setSelectedFromAirport(airport);
      setFromSearch(displayValue);
      setShowFromDropdown(false);
    } else {
      setSelectedToAirport(airport);
      setToSearch(displayValue);
      setShowToDropdown(false);
    }
  };

  // Swap airports
  const swapAirports = () => {
    const tempFrom = selectedFromAirport;
    const tempFromSearch = fromSearch;
    
    setSelectedFromAirport(selectedToAirport);
    setFromSearch(toSearch);
    setSelectedToAirport(tempFrom);
    setToSearch(tempFromSearch);
  };

  // Handle manual search
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    
    if (!selectedFromAirport) {
      alert("Please select departure airport");
      return;
    }
    
    if (!selectedToAirport) {
      alert("Please select destination airport");
      return;
    }
    
    if (!travelDate) {
      alert("Please select travel date");
      return;
    }
    
    if (passengers < 1) {
      alert("Please enter at least 1 passenger");
      return;
    }
    
    setAutoSearchTriggered(false);
    performSearch(selectedFromAirport.airportCode, selectedToAirport.airportCode, travelDate, passengers);
  };

  // Handle booking - SIMPLIFIED: Direct to payment
  const handleBookFare = (fare) => {
    // Directly navigate to payment screen
    navigate("/seriesfare-payment", {
      state: {
        fare: fare,
        searchQuery: {
          from: selectedFromAirport?.airportCode,
          to: selectedToAirport?.airportCode,
          date: travelDate.toISOString().split('T')[0]
        },
        bookingPax: passengers, // Use search passengers
        // No passenger details required
      }
    });
  };

  // Go back
  const handleGoBack = () => {
    navigate(-1);
  };

  // Apply filters
  useEffect(() => {
    let filtered = searchResults;
    
    if (airlineFilter) {
      filtered = filtered.filter(fare => 
        fare.airlineCode?.toLowerCase().includes(airlineFilter.toLowerCase())
      );
    }
    
    if (companyFilter) {
      filtered = filtered.filter(fare => 
        fare.companyName?.toLowerCase().includes(companyFilter.toLowerCase())
      );
    }
    
    setFilteredResults(filtered);
  }, [airlineFilter, companyFilter, searchResults]);

  // Sort by price
  const sortByPrice = () => {
    const sorted = [...filteredResults].sort((a, b) => {
      const priceA = a.pricePerPerson || 0;
      const priceB = b.pricePerPerson || 0;
      
      if (priceSort === "asc") {
        return priceA - priceB;
      } else {
        return priceB - priceA;
      }
    });
    setFilteredResults(sorted);
    setPriceSort(priceSort === "asc" ? "desc" : "asc");
  };

  // Reset search
  const resetSearch = () => {
    setFromSearch("");
    setToSearch("");
    setSelectedFromAirport(null);
    setSelectedToAirport(null);
    setTravelDate(new Date());
    setPassengers(1);
    setSearchResponse(null);
    setSearchResults([]);
    setFilteredResults([]);
    setSearchPerformed(false);
    setAutoSearchTriggered(false);
    setAirlineFilter("");
    setCompanyFilter("");
    setPriceSort("asc");
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return `${userCurrency} 0`;
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    return `${userCurrency} ${numAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}`;
  };

  // Calculate days remaining
  const getDaysRemaining = (validTo) => {
    if (!validTo) return 0;
    const today = new Date();
    const validDate = new Date(validTo);
    const diffTime = validDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.airport-dropdown')) {
        setShowFromDropdown(false);
        setShowToDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) return <div className="text-center py-5">Loading...</div>;

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <FlighTabs pageName="AGENT SEARCH" />
                </div>
                
                {/* Search Form */}
                <div className="card-body">
                  <div className="mb-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="flex-grow-1">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm mb-3"
                          onClick={handleGoBack}
                        >
                          <RiArrowLeftLine className="me-2" />
                          Back to Flight Search
                        </button>
                        <h5 className="card-title text-primary mb-0">
                          <RiPriceTagLine className="me-2" />
                          Series Fares Search
                        </h5>
                        <p className="text-muted mb-0">
                          Find guaranteed fares for your clients
                        </p>
                      </div>
                    </div>
                    
                    <form onSubmit={handleSearch}>
                      <div className="row g-3 mb-4">
                        {/* From Airport */}
                        <div className="col-md-3">
                          <label className="form-label fw-bold">
                            <RiFlightTakeoffLine className="me-1" /> From *
                          </label>
                          <div className="airport-dropdown">
                            <div className="input-group">
                              <span className="input-group-text bg-light">
                                <RiFlightTakeoffLine />
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Departure airport..."
                                value={fromSearch}
                                onChange={(e) => {
                                  setFromSearch(e.target.value);
                                  filterAirports(e.target.value, setFilteredFromAirports);
                                  setShowFromDropdown(true);
                                }}
                                onFocus={() => {
                                  setShowFromDropdown(true);
                                  filterAirports(fromSearch, setFilteredFromAirports);
                                }}
                                required
                              />
                            </div>
                            
                            {showFromDropdown && filteredFromAirports.length > 0 && (
                              <div className="position-relative">
                                <div className="dropdown-menu show w-100 mt-1" style={{ zIndex: 1050 }}>
                                  {filteredFromAirports.map((airport) => (
                                    airport && (
                                      <button
                                        key={`from-${airport.airportCode}`}
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => handleAirportSelect(airport, 'from')}
                                      >
                                        <div>
                                          <strong>{airport.cityName} ({airport.airportCode})</strong>
                                          <div className="small text-muted">
                                            {airport.airportName}, {airport.countryName}
                                          </div>
                                        </div>
                                      </button>
                                    )
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* To Airport */}
                        <div className="col-md-3 position-relative">
                          <label className="form-label fw-bold">
                            <RiFlightLandLine className="me-1" /> To *
                          </label>
                          <div className="airport-dropdown">
                            <div className="input-group">
                              <span className="input-group-text bg-light">
                                <RiFlightLandLine />
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Destination airport..."
                                value={toSearch}
                                onChange={(e) => {
                                  setToSearch(e.target.value);
                                  filterAirports(e.target.value, setFilteredToAirports);
                                  setShowToDropdown(true);
                                }}
                                onFocus={() => {
                                  setShowToDropdown(true);
                                  filterAirports(toSearch, setFilteredToAirports);
                                }}
                                required
                              />
                            </div>
                            
                            {showToDropdown && filteredToAirports.length > 0 && (
                              <div className="position-relative">
                                <div className="dropdown-menu show w-100 mt-1" style={{ zIndex: 1050 }}>
                                  {filteredToAirports.map((airport) => (
                                    airport && (
                                      <button
                                        key={`to-${airport.airportCode}`}
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => handleAirportSelect(airport, 'to')}
                                      >
                                        <div>
                                          <strong>{airport.cityName} ({airport.airportCode})</strong>
                                          <div className="small text-muted">
                                            {airport.airportName}, {airport.countryName}
                                          </div>
                                        </div>
                                      </button>
                                    )
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Swap Button */}
                            {selectedFromAirport && selectedToAirport && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm position-absolute"
                                style={{
                                  right: '10px',
                                  top: '33px',
                                  zIndex: 100
                                }}
                                onClick={swapAirports}
                                title="Swap airports"
                              >
                                <RiArrowUpDownLine />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Travel Date */}
                        <div className="col-md-2">
                          <label className="form-label fw-bold">
                            <RiCalendarLine className="me-1" /> Travel Date *
                          </label>
                          <DatePicker
                            selected={travelDate}
                            onChange={handleTravelDateChange}
                            customInput={<CustomDateInput />}
                            dateFormat="dd-MMM-yyyy"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            minDate={new Date()}
                            yearDropdownItemNumber={50}
                            scrollableYearDropdown
                            placeholderText="Select date"
                          />
                        </div>
                        
                        {/* Passengers */}
                        <div className="col-md-2">
                          <label className="form-label fw-bold">
                            <RiUserLine className="me-1" /> Passengers *
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            value={passengers}
                            onChange={(e) => setPassengers(Math.max(1, parseInt(e.target.value) || 1))}
                            min="1"
                            max="100"
                            required
                          />
                        </div>
                        
                        {/* Search Button */}
                        <div className="col-md-2 d-flex align-items-end">
                          <button
                            type="submit"
                            className="btn btn-primary w-100"
                            disabled={loadingResults}
                          >
                            {loadingResults ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Searching...
                              </>
                            ) : (
                              <>
                                <RiSearchLine className="me-2" />
                                Search 
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* Selected Route Display */}
                      {selectedFromAirport && selectedToAirport && (
                        <div className="alert alert-primary mb-0">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <strong>
                                {selectedFromAirport.cityName} ({selectedFromAirport.airportCode})
                                <RiArrowRightLine className="mx-2" />
                                {selectedToAirport.cityName} ({selectedToAirport.airportCode})
                              </strong>
                              {travelDate && (
                                <span className="ms-3">
                                  <RiCalendarLine className="me-1" />
                                  {formatDate(travelDate.toISOString())}
                                </span>
                              )}
                              <span className="ms-3">
                                <RiUserLine className="me-1" />
                                {passengers} {passengers === 1 ? 'Passenger' : 'Passengers'}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={resetSearch}
                            >
                              Clear Search
                            </button>
                          </div>
                        </div>
                      )}
                    </form>
                  </div>
                  
                  {/* Results Section */}
                  {searchPerformed && (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h5 className="mb-0">
                            <RiPriceTagLine className="me-2 text-success" />
                            Available Series Fares
                            {searchResponse && (
                              <span className="badge bg-primary ms-2">
                                {searchResponse.count || 0} fares found
                              </span>
                            )}
                          </h5>
                          <p className="text-muted mb-0">
                            Price shown is per person • Book with one click
                          </p>
                        </div>
                        
                        {/* Filter and Sort Controls */}
                        {searchResults.length > 0 && (
                          <div className="d-flex gap-3">
                            <div className="input-group" style={{width: '200px'}}>
                              <span className="input-group-text">
                                <RiFilterLine />
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Filter by airline..."
                                value={airlineFilter}
                                onChange={(e) => setAirlineFilter(e.target.value)}
                              />
                            </div>
                            
                            <div className="input-group" style={{width: '200px'}}>
                              <span className="input-group-text">
                                <RiBuildingLine />
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Filter by DMC..."
                                value={companyFilter}
                                onChange={(e) => setCompanyFilter(e.target.value)}
                              />
                            </div>
                            
                            <button
                              type="button"
                              className="btn btn-outline-primary d-flex align-items-center"
                              onClick={sortByPrice}
                            >
                              {priceSort === "asc" ? <RiSortAsc /> : <RiSortDesc />}
                              <span className="ms-2">Price</span>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Loading State */}
                      {loadingResults && (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2">
                            {autoSearchTriggered ? "Loading results from your flight search..." : "Searching for available fares..."}
                          </p>
                        </div>
                      )}
                      
                      {/* No Results */}
                      {!loadingResults && filteredResults.length === 0 && (
                        <div className="text-center py-5">
                          <div className="display-1 text-muted mb-3">
                            <RiPriceTagLine />
                          </div>
                          <h5>No Series Fares Found</h5>
                          <p className="text-muted">
                            {searchResults.length === 0 
                              ? "No series fares available for this route and date. Try different search criteria."
                              : "No fares match your filter criteria. Try different filters."
                            }
                          </p>
                          {searchResults.length === 0 && (
                            <button
                              type="button"
                              className="btn btn-primary mt-3"
                              onClick={handleSearch}
                            >
                              Try Search Again
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* Results Table */}
                      {!loadingResults && filteredResults.length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-hover table-bordered align-middle">
                            <thead className="table-light">
                              <tr>
                                <th width="100">Airline</th>
                                <th width="150">Route</th>
                                <th width="150" className="text-end">Price per Person</th>
                                <th width="100" className="text-center">Available Seats</th>
                                <th width="150" className="text-center">DMC Provider</th>
                                <th width="200" className="text-center">Validity Period</th>
                                <th width="120" className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredResults.map((fare, index) => {
                                const daysRemaining = getDaysRemaining(fare.validTo);
                                const totalForPassengers = (fare.pricePerPerson || 0) * passengers;
                                
                                return (
                                  <tr key={index} className="hover-row">
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '32px', height: '32px'}}>
                                          <RiPlaneLine />
                                        </div>
                                        <div className="ms-2">
                                          <strong>{fare.airlineCode}</strong>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <div className="text-center">
                                          <div className="fw-bold">{fare.from}</div>
                                          <small className="text-muted">Departure</small>
                                        </div>
                                        <RiArrowRightLine className="mx-3 text-primary" />
                                        <div className="text-center">
                                          <div className="fw-bold">{fare.to}</div>
                                          <small className="text-muted">Arrival</small>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="text-end">
                                      <div className="fw-bold text-success fs-5">
                                        {formatCurrency(fare.pricePerPerson)}
                                      </div>
                                      <div className="small text-muted">
                                        For {passengers} pax: {formatCurrency(totalForPassengers)}
                                      </div>
                                    </td>
                                    
                                    <td className="text-center">
                                      <div className="d-flex align-items-center justify-content-center">
                                        <div>
                                          <div className="fw-bold">{fare.availableSeats}</div>
                                          <small className="text-muted">Seats</small>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="text-center">
                                      {fare.companyName ? (
                                        <div className="d-flex flex-column align-items-center">
                                          <span className="badge bg-info mb-1">
                                            <RiBuildingLine className="me-1" />
                                            {fare.companyName}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="d-flex flex-column align-items-center">
                                          <span className="text-muted">-</span>
                                          <small className="text-muted">Direct</small>
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <div className="small">
                                        <div className="d-flex justify-content-between">
                                          <span>Valid From:</span>
                                          <span className="fw-bold">{formatDate(fare.validFrom)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mt-1">
                                          <span>Valid To:</span>
                                          <span className="fw-bold">{formatDate(fare.validTo)}</span>
                                        </div>
                                      
                                        {daysRemaining > 0 && (
                                          <div className="mt-2 text-center">
                                            <span className="badge bg-warning text-dark">
                                              <RiTimerLine className="me-1" />
                                              {daysRemaining} days left
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="text-center">
  <div className="d-flex flex-column gap-2">
    <button
      type="button"
      className={`btn btn-sm ${fare.availableSeats < passengers 
        ? "btn-danger disabled" 
        : "btn-warning"}`}
      onClick={() => handleBlockFare(fare)}
      disabled={fare.availableSeats < passengers}
      title={fare.availableSeats < passengers 
        ? `Only ${fare.availableSeats} seats available` 
        : "Block this fare"}
    >
   
      {fare.availableSeats < passengers ? "Sold Out" : "Block"}
    </button>
    <small className="text-muted">
      Total package: {formatCurrency(fare.pricePerPerson * fare.availableSeats)}
    </small>
  </div>
</td>

                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Initial State */}
                  {!searchPerformed && !loadingResults && (
                    <div className="text-center py-5">
                      <div className="display-1 text-muted mb-3">
                        <RiSearchLine />
                      </div>
                      <h5>Search for Series Fares</h5>
                      <p className="text-muted">
                        Enter your search criteria to find guaranteed fares for your clients
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesFareAgentSearch;
